'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './LoadingRegion.module.css';

export function useDelayedLoading(pending: boolean) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setVisible(true), 200);
    return () => {
      window.clearTimeout(timer);
      setVisible(false);
    };
  }, [pending]);
  return pending && visible;
}

export function LoadingStatus({
  label = 'Daten werden geladen…',
  announce = true,
  size = 'small',
}: {
  label?: string;
  announce?: boolean;
  size?: 'small' | 'large';
}) {
  return (
    <span
      role={announce ? 'status' : undefined}
      aria-live={announce ? 'polite' : undefined}
      aria-hidden={announce ? undefined : true}
      className="inline-flex items-center justify-center align-middle"
    >
      <span
        aria-hidden="true"
        className={`${styles.letter} ${size === 'large' ? styles.large : ''}`}
      >
        <span className={styles.shape} />
      </span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function InlineLoading({
  pending,
  label,
}: {
  pending: boolean;
  label?: string;
}) {
  const visible = useDelayedLoading(pending);
  return visible ? <LoadingStatus label={label} /> : null;
}

export function LoadingRegion({
  pending,
  children,
  label,
  error,
  onRetry,
  className = '',
}: {
  pending: boolean;
  children?: ReactNode;
  label?: string;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  dark?: boolean;
}) {
  const visible = useDelayedLoading(pending);
  const regionRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const region = regionRef.current;
    const badge = badgeRef.current;
    if (!region || !badge) return;
    const center = () => {
      const rect = region.getBoundingClientRect();
      const top = Math.max(0, rect.top);
      const bottom = Math.min(window.innerHeight, rect.bottom);
      // Use the visible slice, so long lists do not put the loader below the fold.
      const midpoint = bottom > top ? (top + bottom) / 2 - rect.top : rect.height / 2;
      badge.style.top = `${midpoint}px`;
    };
    center();
    window.addEventListener('scroll', center, { capture: true, passive: true });
    window.addEventListener('resize', center);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(center);
    observer?.observe(region);
    return () => {
      window.removeEventListener('scroll', center, true);
      window.removeEventListener('resize', center);
      observer?.disconnect();
    };
  }, [visible]);
  return (
    <div className={className}>
      {error && !pending && (
        <div
          role="alert"
          className="my-3 border border-signal/40 p-3 font-mono text-sm text-signal"
        >
          <p>{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 border border-current px-3 py-1 text-xs uppercase hover:underline"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      )}
      <div ref={regionRef} className={`relative ${visible ? 'min-h-20' : ''}`}>
        <div aria-busy={pending} className={visible ? 'opacity-40' : undefined}>
          {children}
        </div>
        {visible && (
          <div className="pointer-events-none absolute inset-0 z-10 flex justify-center">
            <div
              ref={badgeRef}
              className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
            >
              <LoadingStatus label={label} size="large" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function PageLoading({ dark = false }: { dark?: boolean }) {
  return (
    <LoadingRegion
      pending
      dark={dark}
      label="Seite wird geladen…"
      className="min-h-[40vh] [&>div:last-child]:min-h-[40vh]"
    />
  );
}
